import { blueprints, modules as allModules } from "./data.ts";
import type { ModuleDef, Ship, ShipBuildSlot, ShipBulkhead } from "./types.ts";

export interface SlotFitResult {
  ok: boolean;
  reason: string | null;
}

export interface BuildFit {
  slot: ShipBuildSlot;
  module: ModuleDef | null;
  bulkhead: ShipBulkhead | null;
  experimentalKey?: string | null;
  engineering?: {
    blueprintFdName: string | null;
    blueprintName: string | null;
    grade: number | null;
  };
}

export interface BuildStats {
  moduleCount: number;
  price: number;
  unladenMass: number;
  ladenMass: number;
  cargoCapacity: number;
  fuelCapacity: number;
  fuelReserve: number;
  jumpMax: number | null;
  jumpUnladen: number | null;
  jumpLaden: number | null;
  rangeUnladen: number | null;
  rangeLaden: number | null;
  speed: number | null;
  boost: number | null;
  shield: number | null;
  armour: number | null;
  armourKineticResistance: number | null;
  armourThermalResistance: number | null;
  armourExplosiveResistance: number | null;
  armourCausticResistance: number | null;
  powerCapacity: number | null;
  powerDraw: number | null;
  powerRetracted: number | null;
  warnings: string[];
}

const CORE_GROUP_LABELS: Record<string, string> = {
  pp: "Power Plant",
  t: "Thrusters",
  fsd: "Frame Shift Drive",
  ls: "Life Support",
  pd: "Power Distributor",
  s: "Sensors",
  ft: "Fuel Tank",
};

function stat(module: ModuleDef | null, key: string): number | null {
  const value = module?.stats[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function hasEligibleGroup(slot: ShipBuildSlot, module: ModuleDef): boolean {
  if (!slot.eligible) return true;
  return module.group !== null && slot.eligible.includes(module.group);
}

export function moduleFitsSlot(
  ship: Ship,
  slot: ShipBuildSlot,
  module: ModuleDef,
): SlotFitResult {
  if (module.category === "mercgear") {
    return { ok: false, reason: "On-foot equipment cannot be fitted to a ship." };
  }

  if (slot.kind === "bulkhead") {
    if (module.category !== "standard" || module.ship !== ship.name) {
      return { ok: false, reason: `Requires a bulkhead for the ${ship.name}.` };
    }
    return { ok: true, reason: null };
  }

  if (slot.kind === "core") {
    if (module.category !== "standard" || module.group !== slot.group) {
      return {
        ok: false,
        reason: `Requires a ${CORE_GROUP_LABELS[slot.group ?? ""] ?? "core"} module.`,
      };
    }
    if (slot.size !== null && module.class !== slot.size) {
      return { ok: false, reason: `Requires class ${slot.size}.` };
    }
    if (module.group === "sg" && stat(module, "maxmass") !== null) {
      const maxMass = stat(module, "maxmass") as number;
      if (maxMass < (ship.hullMass ?? 0)) {
        return { ok: false, reason: "Shield generator maximum mass is below the hull mass." };
      }
    }
    return { ok: true, reason: null };
  }

  if (slot.kind === "hardpoint") {
    if (module.category !== "hardpoint") {
      return { ok: false, reason: "Requires a hardpoint weapon." };
    }
    if (slot.size !== null && module.class !== null && module.class > slot.size) {
      return { ok: false, reason: `Requires class ${slot.size} or smaller.` };
    }
    if (!hasEligibleGroup(slot, module)) {
      return { ok: false, reason: "Not eligible for this hardpoint." };
    }
    return { ok: true, reason: null };
  }

  if (slot.kind === "utility") {
    if (module.category !== "utility") {
      return { ok: false, reason: "Requires a utility module." };
    }
    return { ok: true, reason: null };
  }

  if (module.category !== "internal" && !(module.category === "standard" && module.group === "ft")) {
    return { ok: false, reason: "Requires an optional internal module." };
  }
  if (slot.size !== null && module.class !== null && module.class > slot.size) {
    return { ok: false, reason: `Requires class ${slot.size} or smaller.` };
  }
  if (!hasEligibleGroup(slot, module)) {
    return { ok: false, reason: "Not eligible for this restricted slot." };
  }
  if (module.group === "sg" && stat(module, "maxmass") !== null) {
    const maxMass = stat(module, "maxmass") as number;
    if (maxMass < (ship.hullMass ?? 0)) {
      return { ok: false, reason: "Shield generator maximum mass is below the hull mass." };
    }
  }
  return { ok: true, reason: null };
}

export function modulesForSlot(ship: Ship, slot: ShipBuildSlot): ModuleDef[] {
  if (slot.kind === "bulkhead") {
    return allModules
      .filter((module) => module.category === "standard" && module.ship === ship.name)
      .sort((a, b) => (a.class ?? 0) - (b.class ?? 0));
  }
  return allModules
    .filter((module) => moduleFitsSlot(ship, slot, module).ok)
    .sort((a, b) => {
      const classOrder = (b.class ?? 0) - (a.class ?? 0);
      if (classOrder !== 0) return classOrder;
      return (a.rating ?? "").localeCompare(b.rating ?? "");
    });
}

export function massCurveMultiplier(
  mass: number,
  minMass: number,
  optMass: number,
  maxMass: number,
  minMultiplier: number,
  optMultiplier: number,
  maxMultiplier: number,
): number {
  if (maxMass <= minMass) return optMultiplier;
  const range = maxMass - minMass;
  const ratio = Math.max(0, Math.min(1, (maxMass - mass) / range));
  const numerator = Math.max(0.000001, optMultiplier - minMultiplier);
  const denominator = Math.max(0.000001, maxMultiplier - minMultiplier);
  const optRatio = optMass === minMass ? 1 : Math.max(0.000001, (maxMass - optMass) / range);
  const exponent = Math.log(numerator / denominator) / Math.log(optRatio);
  return minMultiplier + Math.pow(ratio, exponent) * (maxMultiplier - minMultiplier);
}

export function jumpDistance(
  mass: number,
  fuel: number,
  maxFuelPerJump: number,
  optimalMass: number,
  fuelMultiplier: number,
  fuelPower: number,
): number | null {
  if (mass <= 0 || fuel <= 0 || maxFuelPerJump <= 0 || optimalMass <= 0) return null;
  if (fuelMultiplier <= 0 || fuelPower <= 0) return null;
  const used = Math.min(fuel, maxFuelPerJump);
  return Math.pow(used / fuelMultiplier, 1 / fuelPower) * optimalMass / (mass + used);
}

export function jumpRange(
  mass: number,
  fuelCapacity: number,
  maxFuelPerJump: number,
  optimalMass: number,
  fuelMultiplier: number,
  fuelPower: number,
): number | null {
  let remaining = fuelCapacity;
  let range = 0;
  for (let jumps = 0; remaining >= 0.000001 && jumps < 10000; jumps += 1) {
    const distance = jumpDistance(
      mass,
      remaining,
      maxFuelPerJump,
      optimalMass,
      fuelMultiplier,
      fuelPower,
    );
    if (distance === null) break;
    range += distance;
    remaining -= Math.min(remaining, maxFuelPerJump);
  }
  return range > 0 ? range : null;
}

function moduleMass(module: ModuleDef | null): number {
  return module?.mass ?? 0;
}

function modulePower(module: ModuleDef | null): number {
  return module?.power ?? 0;
}

export type EngineeringRoll = "minimum" | "maximum";

export function applyEngineeringModifiers(
  module: ModuleDef,
  engineering: NonNullable<BuildFit["engineering"]>,
  roll: EngineeringRoll = "maximum",
): { module: ModuleDef; incomplete: boolean } {
  if (module.preEngineered || (!engineering.blueprintFdName && !engineering.blueprintName)) {
    return { module, incomplete: false };
  }
  if (!Number.isInteger(engineering.grade) || engineering.grade === null) {
    return { module, incomplete: true };
  }

  const candidates = blueprints.filter((blueprint) =>
    module.group !== null && blueprint.moduleKeys.includes(module.group)
  );
  const fdName = engineering.blueprintFdName?.trim().toLowerCase();
  const name = engineering.blueprintName?.trim().toLowerCase();
  const byFdName = fdName
    ? candidates.filter((blueprint) => blueprint.fdName?.toLowerCase() === fdName)
    : [];
  const byName = name
    ? candidates.filter((blueprint) =>
      blueprint.name.toLowerCase() === name || blueprint.displayName.toLowerCase() === name
    )
    : [];
  const blueprint = byName.length === 1
    ? byName[0]
    : byFdName.length === 1
      ? byFdName[0]
      : undefined;
  const grade = blueprint?.grades.find((candidate) => candidate.grade === engineering.grade);
  if (!grade) return { module, incomplete: true };

  let mass = module.mass;
  let power = module.power;
  let integrity = module.integrity;
  const stats = { ...module.stats };
  let incomplete = false;

  for (const [property, rawValues] of Object.entries(grade.modifiers)) {
    const values = Array.isArray(rawValues)
      ? rawValues.filter((value): value is number => typeof value === "number" && Number.isFinite(value))
      : typeof rawValues === "number" && Number.isFinite(rawValues)
        ? [rawValues]
        : [];
    if (values.length === 0) {
      incomplete = true;
      continue;
    }

    const modifier = values[roll === "minimum" ? 0 : values.length - 1];
    if (modifier === undefined) {
      incomplete = true;
      continue;
    }
    const current = property === "mass"
      ? mass
      : property === "power"
        ? power
        : property === "integrity"
          ? integrity
          : stats[property];
    if (typeof current !== "number" || !Number.isFinite(current)) {
      incomplete = true;
      continue;
    }

    const adjusted = current * (1 + modifier);
    if (property === "mass") mass = adjusted;
    else if (property === "power") power = adjusted;
    else if (property === "integrity") integrity = adjusted;
    else stats[property] = adjusted;
  }

  return { module: { ...module, mass, power, integrity, stats }, incomplete };
}

export function computeBuildStats(
  ship: Ship,
  fits: BuildFit[],
  options: { cargo?: number; fuel?: number; engineeringRoll?: EngineeringRoll } = {},
): BuildStats {
  const engineeringWarnings = new Set<string>();
  const engineeringRoll = options.engineeringRoll ?? "maximum";
  const modules = fits.map((fit) => {
    if (!fit.module || !fit.engineering) return fit.module;
    const result = applyEngineeringModifiers(fit.module, fit.engineering, engineeringRoll);
    if (result.incomplete) {
      engineeringWarnings.add("Some engineering modifiers could not be applied from the verified blueprint data.");
    }
    return result.module;
  }).filter((module): module is ModuleDef => Boolean(module));
  if (fits.some((fit) => fit.experimentalKey)) {
    engineeringWarnings.add(
      "Experimental-effect numerical modifiers are unavailable in the verified game data.",
    );
  }
  const bulkhead = fits.find((fit) => fit.slot.kind === "bulkhead")?.bulkhead ?? null;
  const fsd = modules.find((module) => module.group === "fsd") ?? null;
  const thrusters = modules.find((module) => module.group === "t") ?? null;
  const powerPlant = modules.find((module) => module.group === "pp") ?? null;
  const powerDistributor = modules.find((module) => module.group === "pd") ?? null;
  const shieldGenerator = modules.find((module) => module.group === "sg") ?? null;

  const cargoCapacity =
    options.cargo ??
    modules.reduce((total, module) => total + (stat(module, "cargo") ?? 0), 0);
  const fuelCapacity =
    options.fuel ?? modules.reduce((total, module) => total + (stat(module, "fuel") ?? 0), 0);
  const unladenMass =
    (ship.hullMass ?? 0) +
    (bulkhead?.mass ?? 0) +
    modules.reduce((total, module) => total + moduleMass(module), 0);
  const ladenMass = unladenMass + cargoCapacity;
  const maxFuelPerJump = stat(fsd, "maxfuel");
  const optimalMass = stat(fsd, "optmass");
  const fuelMultiplier = stat(fsd, "fuelmul");
  const fuelPower = stat(fsd, "fuelpower");

  const jumpMax =
    fsd && maxFuelPerJump !== null && optimalMass !== null && fuelMultiplier !== null && fuelPower !== null
      ? jumpDistance(
          unladenMass,
          Math.min(fuelCapacity, maxFuelPerJump),
          maxFuelPerJump,
          optimalMass,
          fuelMultiplier,
          fuelPower,
        )
      : null;
  const jumpUnladen =
    fsd && maxFuelPerJump !== null && optimalMass !== null && fuelMultiplier !== null && fuelPower !== null
      ? jumpDistance(
          unladenMass,
          fuelCapacity,
          maxFuelPerJump,
          optimalMass,
          fuelMultiplier,
          fuelPower,
        )
      : null;
  const jumpLaden =
    fsd && maxFuelPerJump !== null && optimalMass !== null && fuelMultiplier !== null && fuelPower !== null
      ? jumpDistance(
          ladenMass,
          fuelCapacity,
          maxFuelPerJump,
          optimalMass,
          fuelMultiplier,
          fuelPower,
        )
      : null;
  const rangeUnladen =
    fsd && maxFuelPerJump !== null && optimalMass !== null && fuelMultiplier !== null && fuelPower !== null
      ? jumpRange(
          unladenMass,
          fuelCapacity,
          maxFuelPerJump,
          optimalMass,
          fuelMultiplier,
          fuelPower,
        )
      : null;
  const rangeLaden =
    fsd && maxFuelPerJump !== null && optimalMass !== null && fuelMultiplier !== null && fuelPower !== null
      ? jumpRange(
          ladenMass,
          fuelCapacity,
          maxFuelPerJump,
          optimalMass,
          fuelMultiplier,
          fuelPower,
        )
      : null;

  const thrusterMass = ladenMass + fuelCapacity;
  const minMass = stat(thrusters, "minmass");
  const thrusterOptMass = stat(thrusters, "optmass");
  const thrusterMaxMass = stat(thrusters, "maxmass");
  const minMultiplier = stat(thrusters, "minmul");
  const optMultiplier = stat(thrusters, "optmul");
  const maxMultiplier = stat(thrusters, "maxmul");
  const thrustMultiplier =
    thrusters &&
    minMass !== null &&
    thrusterOptMass !== null &&
    thrusterMaxMass !== null &&
    minMultiplier !== null &&
    optMultiplier !== null &&
    maxMultiplier !== null
      ? massCurveMultiplier(
          thrusterMass,
          minMass,
          thrusterOptMass,
          thrusterMaxMass,
          minMultiplier,
          optMultiplier,
          maxMultiplier,
        )
      : null;
  const speed = ship.speed !== null && thrustMultiplier !== null ? ship.speed * thrustMultiplier : null;
  const boostEnergy = ship.boostEnergy;
  const engCap = stat(powerDistributor, "engcap");
  const boost =
    ship.boost !== null &&
    thrustMultiplier !== null &&
    boostEnergy !== null &&
    engCap !== null &&
    engCap >= boostEnergy
      ? ship.boost * thrustMultiplier
      : null;

  const hullMass = (ship.hullMass ?? 0) + (bulkhead?.mass ?? 0);
  const shieldGeneratorMaxMass = stat(shieldGenerator, "maxmass");
  const shieldMinMass = stat(shieldGenerator, "minmass");
  const shieldOptMass = stat(shieldGenerator, "optmass");
  const shieldMinMultiplier = stat(shieldGenerator, "minmul");
  const shieldOptMultiplier = stat(shieldGenerator, "optmul");
  const shieldMaxMultiplier = stat(shieldGenerator, "maxmul");
  const shieldBoost =
    1 +
    modules
      .filter((module) => module.group === "sb")
      .reduce((total, module) => total + (stat(module, "shieldboost") ?? 0), 0);
  const shieldMultiplier =
    shieldGenerator &&
    shieldGeneratorMaxMass !== null &&
    shieldGeneratorMaxMass >= hullMass &&
    shieldMinMass !== null &&
    shieldOptMass !== null &&
    shieldMinMultiplier !== null &&
    shieldOptMultiplier !== null &&
    shieldMaxMultiplier !== null
      ? massCurveMultiplier(
          hullMass,
          shieldMinMass,
          shieldOptMass,
          shieldGeneratorMaxMass,
          shieldMinMultiplier,
          shieldOptMultiplier,
          shieldMaxMultiplier,
        )
      : null;
  const shield =
    ship.baseShieldStrength !== null && shieldMultiplier !== null
      ? ship.baseShieldStrength * shieldMultiplier * shieldBoost
      : null;

  const armourMultiplier = bulkhead?.hullBoost ?? 1;
  const armour = ship.baseArmour !== null ? ship.baseArmour * armourMultiplier : null;

  const powerCapacity = stat(powerPlant, "pgen");
  const powerDraw = modules.reduce(
    (total, module) => total + modulePower(module),
    0,
  );
  const powerRetracted = fits
    .filter((fit) => fit.module && fit.slot.kind !== "hardpoint")
    .reduce((total, fit) => total + modulePower(fit.module), 0);

  const price =
    (ship.retailCost ?? 0) +
    (bulkhead?.cost ?? 0) +
    modules.reduce((total, module) => total + (module.cost ?? 0), 0);

  const warnings: string[] = [...engineeringWarnings];
  if (!powerPlant) warnings.push("No power plant fitted.");
  if (!fsd) warnings.push("No frame shift drive fitted.");
  if (!thrusters) warnings.push("No thrusters fitted.");
  if (fuelCapacity <= 0) warnings.push("No fuel tank fitted.");
  if (powerCapacity !== null && powerDraw > powerCapacity) {
    warnings.push("Power draw exceeds power plant output.");
  }
  if (shieldGenerator && shield === null) {
    warnings.push("Shield generator cannot cover the current hull mass.");
  }
  if (thrusters && thrusterMaxMass !== null && thrusterMass > thrusterMaxMass) {
    warnings.push("Thrusters are overloaded for the current mass.");
  }
  if (boostEnergy !== null && engCap !== null && engCap < boostEnergy) {
    warnings.push("Power distributor cannot sustain boost.");
  }
  if (!bulkhead) warnings.push("No bulkhead fitted; showing base armour.");

  return {
    moduleCount: modules.length,
    price,
    unladenMass,
    ladenMass,
    cargoCapacity,
    fuelCapacity,
    fuelReserve: ship.reserveFuelCapacity ?? 0,
    jumpMax,
    jumpUnladen,
    jumpLaden,
    rangeUnladen,
    rangeLaden,
    speed,
    boost,
    shield,
    armour,
    armourKineticResistance: bulkhead?.kineticResistance ?? null,
    armourThermalResistance: bulkhead?.thermalResistance ?? null,
    armourExplosiveResistance: bulkhead?.explosiveResistance ?? null,
    armourCausticResistance: bulkhead?.causticResistance ?? null,
    powerCapacity,
    powerDraw,
    powerRetracted,
    warnings,
  };
}
